import { Delete, Person, Sync, UploadFileOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import ErrorsTab from "../../../../../components/management/tabs/QuickbooksTabs/ErrorsTab";
import SyncHistoryTab from "../../../../../components/management/tabs/QuickbooksTabs/SyncHistoryTab";
import { deleteCustomer } from "../../../../../api/customers";
import { quickbooksService } from "../../../../../api/quickbooks";
import { QuickBooksSyncModal } from "../../../../../components/shared/QuickBooksSyncModal";
import { ValidationResultModal } from "../../../../../components/shared/ValidationResultModal";
import { assertSectionControllerContract } from "../../../../shared/management/sections/contracts/sectionControllerContract";
import ConfirmDeleteDialog from "../../../../shared/management/shared/ConfirmDeleteDialog";

import QuickBooksTabContainer from "./QuickBooksTabContainer";
import renderQuickbooksCellContent from "./renderQuickbooksCellContent";
import useQuickBooksTabData from "./useQuickBooksTabData";
import useQuickbooksSyncActions from "./useQuickbooksSyncActions";
import useQuickbooksValidation from "./useQuickbooksValidation";

export function useQuickbooksSectionController({
  profile,
  columns,
  rowsPerPage,
  sortConfig,
  onSortChange,
  selectedRows,
  setSelectedRows,
  openDialogManager,
  notify,
}) {
  const quickbooksTabData = useQuickBooksTabData({
    profile,
    rowsPerPage,
    sortConfig,
  });
  const syncHistoryTab = SyncHistoryTab();
  const errorsTab = ErrorsTab();
  const [activeSubTab, setActiveSubTab] = useState("overview");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deletingRow, setDeletingRow] = useState(false);
  const [quickbooksMenuAnchorEl, setQuickbooksMenuAnchorEl] = useState(null);
  const [quickbooksMenuRow, setQuickbooksMenuRow] = useState(null);
  const [quickbooksConnectingCustomerId, setQuickbooksConnectingCustomerId] = useState(null);

  const {
    syncModalOpen,
    syncModalContext,
    syncLoading,
    quickbooksSyncingCustomerIds,
    openSingleSyncModal,
    openBulkSyncModal,
    closeSyncModal,
    handleSyncConfirmed,
    handleExportSelected,
  } = useQuickbooksSyncActions({
    notify,
    refresh: quickbooksTabData.refresh,
    selectedRows,
    setSelectedRows,
    onSortChange,
  });

  const {
    validationModalOpen,
    validationCustomer,
    validationResults,
    validationLoadingCustomerId,
    handleSyncStatusClick,
    handleValidationModalClose,
  } = useQuickbooksValidation({
    notify,
    openSingleSyncModal,
  });

  const handleQuickbooksConnectClick = useCallback(async (row) => {
    const customer = row?.originalCustomer || row;
    const customerId = customer?.id;

    if (!customerId) {
      notify?.({
        open: true,
        message: "Unable to identify customer for QuickBooks connection.",
        severity: "error",
      });
      return;
    }

    setQuickbooksConnectingCustomerId(customerId);

    try {
      sessionStorage.setItem("customer_id", customerId);
      sessionStorage.setItem("qb_connect_origin", "management");
      sessionStorage.setItem("qb_connect_page_params", window.location.search || "");

      const response = await quickbooksService.getAuthUrl(customerId);

      if (response.data?.auth_url) {
        window.location.href = response.data.auth_url;
        return;
      }

      notify?.({
        open: true,
        message: "Unable to start QuickBooks connection.",
        severity: "error",
      });
      setQuickbooksConnectingCustomerId(null);
    } catch (error) {
      notify?.({
        open: true,
        message: error?.response?.data?.error || "Failed to connect to QuickBooks.",
        severity: "error",
      });
      setQuickbooksConnectingCustomerId(null);
    }
  }, [notify]);

  const handleQuickbooksSyncClick = useCallback((row) => {
    const customer = row?.originalCustomer || row;
    openSingleSyncModal(customer);
  }, [openSingleSyncModal]);

  const handleQuickbooksMenuOpen = useCallback((event, row) => {
    setQuickbooksMenuAnchorEl(event?.currentTarget || null);
    setQuickbooksMenuRow(row);
  }, []);

  const renderCellContent = useCallback(
    (value, column, row) => {
      return renderQuickbooksCellContent(value, column, row, {
        quickbooksSyncingCustomerIds,
        validationLoadingCustomerId,
        quickbooksConnectingCustomerId,
        onConnect: handleQuickbooksConnectClick,
        onSync: handleQuickbooksSyncClick,
        onOpenMenu: handleQuickbooksMenuOpen,
        onSyncStatusClick: handleSyncStatusClick,
      });
    },
    [
      handleQuickbooksConnectClick,
      handleQuickbooksMenuOpen,
      handleQuickbooksSyncClick,
      handleSyncStatusClick,
      quickbooksConnectingCustomerId,
      quickbooksSyncingCustomerIds,
      validationLoadingCustomerId,
    ]
  );

  const handleQuickbooksConnect = useCallback(async (dialogData) => {
    const customerId = dialogData?.customerId;
    if (!customerId) {
      notify?.({
        open: true,
        message: "Unable to identify customer for QuickBooks connection.",
        severity: "error",
      });
      return;
    }

    try {
      sessionStorage.setItem("customer_id", customerId);
      sessionStorage.setItem("qb_connect_origin", "management");
      sessionStorage.setItem("qb_connect_page_params", window.location.search || "");

      const response = await quickbooksService.getAuthUrl(customerId);
      if (response.data?.auth_url) {
        window.location.href = response.data.auth_url;
        return;
      }

      notify?.({
        open: true,
        message: "Unable to start QuickBooks connection.",
        severity: "error",
      });
    } catch (error) {
      notify?.({
        open: true,
        message: error?.response?.data?.error || "Failed to connect to QuickBooks.",
        severity: "error",
      });
    }
  }, [notify]);

  const handleQuickbooksDisconnect = useCallback(async (dialogData) => {
    const customerId = dialogData?.customerId;
    if (!customerId) {
      notify({
        open: true,
        message: "Unable to identify customer for QuickBooks disconnect.",
        severity: "error",
      });
      return;
    }

    try {
      await quickbooksService.disconnectQuickbooks(customerId);
      await quickbooksTabData.refresh?.();
      notify({
        open: true,
        message: `QuickBooks connection removed for ${dialogData?.customerName || "this account"}.`,
        severity: "success",
      });
    } catch (error) {
      notify({
        open: true,
        message: error?.response?.data?.error || "Failed to disconnect QuickBooks.",
        severity: "error",
      });
    }
  }, [notify, quickbooksTabData]);

  const handleSyncModalClose = useCallback(() => {
    closeSyncModal();
  }, [closeSyncModal]);

  const handleQuickbooksMenuClose = useCallback(() => {
    setQuickbooksMenuAnchorEl(null);
    setQuickbooksMenuRow(null);
  }, []);

  const handleDeleteRequest = useCallback((row) => {
    if (!row) return;
    setRowToDelete(row);
    setDeleteDialogOpen(true);
    handleQuickbooksMenuClose();
  }, [handleQuickbooksMenuClose]);

  const handleDeleteCancel = useCallback(() => {
    if (deletingRow) return;
    setDeleteDialogOpen(false);
    setRowToDelete(null);
  }, [deletingRow]);

  const handleDeleteConfirm = useCallback(async () => {
    const customerId = rowToDelete?.originalCustomer?.id || rowToDelete?.id;
    if (!customerId || deletingRow) return;

    setDeletingRow(true);
    try {
      await deleteCustomer(customerId);
      await quickbooksTabData.refresh?.();
      notify({
        open: true,
        message: "QuickBooks account deleted successfully!",
        severity: "success",
      });
      setDeleteDialogOpen(false);
      setRowToDelete(null);
      setSelectedRows([]);
    } catch {
      notify({
        open: true,
        message: "Failed to delete QuickBooks account. Please try again.",
        severity: "error",
      });
    } finally {
      setDeletingRow(false);
    }
  }, [deletingRow, notify, quickbooksTabData, rowToDelete, setSelectedRows]);

  const renderRowActionsMenu = useCallback(() => null, []);

  const renderSelectionToolbar = useCallback(
    ({ selectedRows: selected, clearSelection }) => {
      if (activeSubTab !== "overview") return null;
      if (!selected?.length) return null;

      return (
        <>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, color: "#374151", display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <Box component="span" sx={{ fontWeight: 700 }}>
              {selected.length}
            </Box>{" "}
            Accounts Selected
          </Typography>

          <Button
            startIcon={<UploadFileOutlined />}
            onClick={handleExportSelected}
            size="small"
            variant="outlined"
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            Export
          </Button>

          <Button
            startIcon={<Sync />}
            onClick={openBulkSyncModal}
            size="small"
            variant="contained"
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            Bulk QuickBooks Sync
          </Button>

          <Button
            onClick={clearSelection}
            size="small"
            variant="text"
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            Clear
          </Button>
        </>
      );
    },
    [activeSubTab, handleExportSelected, openBulkSyncModal]
  );

  const handleSubTabChange = useCallback((event, newValue) => {
    setActiveSubTab(newValue);
  }, []);

  const currentTabData =
    activeSubTab === "sync-history"
      ? syncHistoryTab
      : activeSubTab === "errors"
      ? errorsTab
      : quickbooksTabData;

  const renderAlternateContent = useCallback(() => {
    return (
      <QuickBooksTabContainer
        columns={columns}
        activeSubTab={activeSubTab}
        tabData={quickbooksTabData}
        rowsPerPage={rowsPerPage}
        sortConfig={sortConfig}
        onSortChange={onSortChange}
        onSubTabChange={handleSubTabChange}
        selectedRows={selectedRows}
        onSelectedRowsChange={setSelectedRows}
        onFiltersChange={quickbooksTabData.handleFiltersChange}
        onDeleteRequest={handleDeleteRequest}
        renderErrorsContent={errorsTab.renderContent}
        renderCellContent={renderCellContent}
        renderRowActionsMenu={renderRowActionsMenu}
        renderSyncHistoryContent={syncHistoryTab.renderContent}
        renderSelectionToolbar={renderSelectionToolbar}
      />
    );
  }, [
    activeSubTab,
    columns,
    errorsTab.renderContent,
    handleDeleteRequest,
    handleSubTabChange,
    quickbooksTabData,
    rowsPerPage,
    sortConfig,
    onSortChange,
    selectedRows,
    setSelectedRows,
    renderCellContent,
    renderRowActionsMenu,
    renderSelectionToolbar,
    syncHistoryTab.renderContent,
  ]);

  const contract = {
    tableProps: {
      hideTable: true,
      renderAlternateContent,
      columns,
      rows: quickbooksTabData.data,
      loading: currentTabData.loading,
      error: currentTabData.error,
      emptyMessage: "No QuickBooks accounts found",
      searchTerm: currentTabData.searchTerm,
      page: quickbooksTabData.page,
      serverSidePagination: true,
      hasNextPage: !!quickbooksTabData.nextCursor,
      hasPrevPage: !!quickbooksTabData.prevCursor,
      onNextPage: quickbooksTabData.handleNextPage,
      onPreviousPage: quickbooksTabData.handlePreviousPage,
      onFiltersChange: quickbooksTabData.handleFiltersChange,
      filterPopoverByField: {},
      onDeleteRequest: handleDeleteRequest,
      renderCellContent,
      renderRowActionsMenu,
    },
    toolbar: {
      searchValue: currentTabData.searchTerm || "",
      onSearchChange: currentTabData.handleSearchChange,
      createAction: null,
      renderTop: null,
      renderSelection: renderSelectionToolbar,
    },
    dialogs: {
      render: () => (
        <>
          <Popover
            open={Boolean(quickbooksMenuAnchorEl)}
            anchorEl={quickbooksMenuAnchorEl}
            onClose={handleQuickbooksMenuClose}
            disablePortal={false}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{ zIndex: 99999 }}
            PaperProps={{
              sx: {
                minWidth: 200,
                boxShadow: "0px 8px 24px rgba(15, 23, 42, 0.2)",
                borderRadius: 2,
                zIndex: 99999,
              },
            }}
          >
            <MenuList sx={{ py: 1 }}>
              <MenuItem
                onClick={() => {
                  const customerId = quickbooksMenuRow?.originalCustomer?.id || quickbooksMenuRow?.id;
                  if (customerId) {
                    window.open(`/client/${customerId}`, "_blank");
                  }
                  handleQuickbooksMenuClose();
                }}
                disabled={!quickbooksMenuRow}
              >
                <ListItemIcon>
                  <Person fontSize="small" sx={{ color: "#374151" }} />
                </ListItemIcon>
                <ListItemText
                  primary="Account Overview"
                  primaryTypographyProps={{ fontSize: "0.875rem" }}
                />
              </MenuItem>
              <MenuItem
                onClick={() => handleDeleteRequest(quickbooksMenuRow)}
                disabled={!quickbooksMenuRow}
                sx={{ color: "#EF4444" }}
              >
                <ListItemIcon>
                  <Delete fontSize="small" sx={{ color: "#EF4444" }} />
                </ListItemIcon>
                <ListItemText
                  primary="Delete"
                  primaryTypographyProps={{ fontSize: "0.875rem" }}
                />
              </MenuItem>
            </MenuList>
          </Popover>

          <QuickBooksSyncModal
            isOpen={syncModalOpen}
            onClose={handleSyncModalClose}
            customer={syncModalContext.customer}
            customerIds={syncModalContext.customerIds}
            selectedCount={syncModalContext.selectedCount}
            onSyncConfirmed={handleSyncConfirmed}
            isAdmin={profile?.type === "admin"}
            isLoading={syncLoading}
          />

          <ValidationResultModal
            isOpen={validationModalOpen}
            onClose={handleValidationModalClose}
            customer={validationCustomer}
            results={validationResults}
            isLoading={Boolean(validationLoadingCustomerId)}
          />

          <ConfirmDeleteDialog
            open={deleteDialogOpen}
            itemLabel={rowToDelete?.accountName || rowToDelete?.name}
            deleting={deletingRow}
            onCancel={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
          />
        </>
      ),
      componentProps: {
        onConnect: handleQuickbooksConnect,
        onDisconnect: handleQuickbooksDisconnect,
      },
    },
    commands: {
      refresh: currentTabData.refresh || quickbooksTabData.refresh,
      exportSelected: handleExportSelected,
      clearSelection: () => setSelectedRows([]),
    },
    meta: {
      sectionId: "quickbooks",
      capabilities: {
        bulkEdit: false,
        sync: true,
      },
    },
  };

  assertSectionControllerContract("quickbooks", contract);
  return contract;
}